import { API_BASE_URL } from "../../constant/constant";
import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Select,
  Upload,
  Form,
  Row,
  Col,
  message,
  Spin,
} from "antd";
import axios from "axios";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InboxOutlined } from "@ant-design/icons";
import BackIcon from "../../assets/back-icon.svg";
import { Container, Content, Header, Heading } from "./Style";
import { handleFileUploadWithSignedURL } from "../Environment/Asconfig";
import { useSnackbar } from "../../context/SnackBarContext";

const { TextArea } = Input;
const { Dragger } = Upload;
const { Option } = Select;

const uploadFileToS3 = async (file) => {
  try {
    const response = await handleFileUploadWithSignedURL(file);
    return response.fileUrl;
  } catch (error) {
    toast.error(error);
    return null;
  }
};

export default function EditSkills() {
  const { t } = useTranslation();
  const { isSidebarOpen } = useOutletContext();
  const navigate = useNavigate();
  const { id } = useParams(); // Fetch skill ID from URL
  const [loading, setLoading] = useState(false);
  const [initialSkill, setInitialSkill] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // Track newly selected files
  const [existingFiles, setExistingFiles] = useState([]); // Track files that were already uploaded
  const [filesToDelete, setFilesToDelete] = useState([]); // Track files marked for deletion
  const [form] = Form.useForm();
  const token = localStorage.getItem("token");
  const showSnackbar = useSnackbar();

  useEffect(() => {
    // Fetch skill data when the component mounts
    const fetchSkillData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/api/skill/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": token || "",
            },
          }
        );

        if (response.data.responseCode === 429){
          showSnackbar(response.data.message, "error");
          return;
        }

        const skillData = response.data.data;
        setInitialSkill(skillData);
        form.setFieldsValue(skillData);
        setExistingFiles(skillData.files || []); // Set existing files from the server
      } catch (error) {
        console.error("Error fetching skill data", error);
        message.error(t("editSkills.messages.fetchFailed"));
      } finally {
        setLoading(false);
      }
    };

    fetchSkillData();
  }, [id, form, t]);

  const handleFileChange = ({ fileList }) => {
    // Update state only with new files to avoid duplicates
    const uniqueFiles = fileList
      .map((file) => file.originFileObj)
      .filter((file) => !selectedFiles.some((f) => f.name === file.name));

    setSelectedFiles((prev) => [...prev, ...uniqueFiles]);
  };

  const handleFileRemove = (file) => {
    // Mark file for deletion
    setFilesToDelete((prev) => [...prev, file]);
    // Remove file from existingFiles list
    setExistingFiles((prev) =>
      prev.filter((f) => f.fileName !== file.fileName)
    );
  };

  const handleNewFileRemove = (fileName) => {
    // Remove file from selectedFiles list
    setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleFinish = async (values) => {
    setLoading(true);

    const uploadedFileUrls = [];
    const userId = localStorage.getItem("id");

    // Upload newly selected files to S3
    for (const file of selectedFiles) {
      try {
        const fileUrl = await uploadFileToS3(file);
        if (!fileUrl) throw new Error("File upload failed");
        uploadedFileUrls.push({
          fileUrl,
          fileName: file.name,
          fileSize: file.size,
          uploadTime: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Error uploading file", error);
        message.error(t("editSkills.messages.uploadFailed", { fileName: file.name }));
      }
    }

    // Prepare the payload to send to the server
    const payload = {
      ...values,
      user: userId,
      files: [...existingFiles, ...uploadedFileUrls], // Merge new and existing files
    };

    try {
      // Send update request with new data
      const response = await axios.put(`${API_BASE_URL}/api/skill/${id}`, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": `${localStorage.getItem("token")}`,
        },
      });

      if (response.data.responseCode === 429){
        showSnackbar(response.data.message, "warning");
        return;
      }
      message.success(t("editSkills.messages.updateSuccess"));

      navigate("/skills"); // Redirect after successful update
    } catch (error) {
      console.error("Failed to update skill", error);
      message.error(t("editSkills.messages.updateFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container isOpen={isSidebarOpen}>
      <Header onClick={() => navigate("/skills")}>
        <img src={BackIcon} alt="back" />
        <Heading>{t("editSkills.title")}</Heading>
      </Header>
      <Content>
        <Spin spinning={loading}>
          <Form
            form={form}
            style={{ padding: "36px 36px" }}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={initialSkill}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.name")}</span>}
                  name="name"
                  rules={[{ required: true, message: t("billing.addSkills.validation.name") }]}
                >
                  <Input placeholder={t("billing.addSkills.placeholders.name")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.model")}</span>}
                  name="model"
                  rules={[{ required: true, message: t("billing.addSkills.validation.model") }]}
                >
                  <Select placeholder={t("billing.addSkills.placeholders.model")}>
                    <Option value="model1">{t("billing.addSkills.models.gpt35")}</Option>
                    <Option value="model2">{t("billing.addSkills.models.gpt4o")}</Option>
                    <Option value="model3">{t("billing.addSkills.models.gpt4oMini")}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.objectives")}</span>}
                  name="objectives"
                  rules={[
                    { required: true, message: t("billing.addSkills.validation.objectives") },
                  ]}
                >
                  <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.taskGuidelines")}</span>
                  }
                  name="taskGuidelines"
                >
                  <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.requiredOutput")}</span>
                  }
                  name="requiredOutput"
                  rules={[
                    { required: true, message: t("billing.addSkills.validation.requiredOutput") },
                  ]}
                >
                  <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <span style={{ fontWeight: 600 }}>
                      {t("billing.addSkills.form.otherRules")}
                    </span>
                  }
                  name="otherRules"
                >
                  <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
                </Form.Item>
              </Col>
            </Row>

            {/* Add new Query text area */}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.query")}</span>}
                  name="query"
                >
                  <TextArea placeholder={t("billing.addSkills.placeholders.query")} rows={4} />
                </Form.Item>
              </Col>
            </Row>

            {/* File Upload */}
            <Form.Item
              label={<span style={{ fontWeight: 600 }}>{t("editSkills.uploadNewFiles")}</span>}
            >
              <Row gutter={16}>
                <Col span={existingFiles.length > 0 || selectedFiles.length > 0 ? 14 : 24}>
                  <Dragger
                    name="files"
                    multiple
                    accept=".pdf"
                    beforeUpload={() => false}
                    onChange={handleFileChange}
                    showUploadList={false}
                    fileList={[]}
                    style={{ textAlign: "center" }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      {t("billing.addSkills.upload.dragDrop")} <a>{t("billing.addSkills.upload.browse")}</a>
                    </p>
                    <p className="ant-upload-hint">{t("billing.addSkills.upload.support")}</p>
                  </Dragger>
                </Col>
                {(existingFiles.length > 0 || selectedFiles.length > 0) && (
                  <Col span={10}>
                    <div
                      style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: "8px",
                        padding: "16px",
                        minHeight: "200px",
                        maxHeight: "300px",
                        overflowY: "auto",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: "12px" }}>
                        {t("editSkills.uploadedFiles")}
                      </div>
                      {existingFiles.map((file, index) => (
                        <div
                          key={`existing-${index}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 12px",
                            marginBottom: "8px",
                            backgroundColor: "#fff",
                            borderRadius: "4px",
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <span
                            style={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {file.fileName}
                          </span>
                          <Button
                            onClick={() => handleFileRemove(file)}
                            type="link"
                            danger
                            size="small"
                            style={{ padding: "0 8px" }}
                          >
                            {t("editSkills.remove")}
                          </Button>
                        </div>
                      ))}
                      {selectedFiles.map((file, index) => (
                        <div
                          key={`new-${index}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 12px",
                            marginBottom: "8px",
                            backgroundColor: "#e6f7ff",
                            borderRadius: "4px",
                            border: "1px solid #91d5ff",
                          }}
                        >
                          <span
                            style={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {file.name} <span style={{ color: "#1890ff", fontSize: "12px" }}>({t("editSkills.new")})</span>
                          </span>
                          <Button
                            onClick={() => handleNewFileRemove(file.name)}
                            type="link"
                            danger
                            size="small"
                            style={{ padding: "0 8px" }}
                          >
                            {t("editSkills.remove")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Col>
                )}
              </Row>
            </Form.Item>

            <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                {loading ? t("editSkills.updating") : t("editSkills.update")}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Content>
    </Container>
  );
}
